import React, { useEffect, useRef, useState } from "react";
import S from "./Quote.styled";
import { Flex, MainInner, MainSection, motionAnimation } from "@/styles/global";
import { H3, Body, Quote as QuoteTypography } from "@/styles/typography";
import { Icon, Image, Link, Wysiwyg } from "@/styles/components";
import { IWPImage, IWPVideo, ColorOptions } from "@/src/types";
import Youtube from "@/components/global-components/Youtube";
import { isLight } from "@/src/helpers";
import { motion } from "framer-motion";

export interface QuoteProps {
  blockId: string;
  backgroundColor: ColorOptions;
  backgroundType: "card" | "fill";
  quotes: {
    mediaType: "image" | "video";
    video: IWPVideo;
    image: {
      image: IWPImage;
      imageText?: string;
      textBackground: "fill" | "gradient";
    };
    title: string;
    quote: string;
    text: string;
  }[];
}

const Quote: React.FC<QuoteProps> = ({
  quotes,
  backgroundColor,
  backgroundType,
  blockId = "",
}) => {
  backgroundColor = backgroundColor || "transparent";
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = quotes?.length > 1;
  const textColor = !backgroundColor
    ? "black"
    : backgroundColor === "yellow"
      ? "brown"
      : isLight(backgroundColor)
        ? "black"
        : "white";
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollLeft = e.currentTarget.scrollLeft;
    const width = e.currentTarget.clientWidth;
    const newSlide = Math.round(scrollLeft / width);
    setCurrentSlide(newSlide);
  };

  const scrollToSlide = (index: number) => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      const slideWidth = scrollContainer.clientWidth;
      const scrollPosition = index * slideWidth;
      scrollContainer.scrollTo({
        left: scrollPosition,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const nextSlide = (currentSlide + 1) % quotes.length;
      scrollToSlide(nextSlide);
    }, 10000);

    return () => clearInterval(interval);
  }, [currentSlide, quotes.length]);

  const renderQuotes = quotes?.map(
    ({ image, title, quote, text, video, mediaType }, i) => (
      <S.Card key={i} id={`slide-${i}`}>
        <S.Inner>
          <S.CardInner
            $backgroundColor={backgroundColor}
            $backgroundType={backgroundType}
          >
            <S.ImageContainer $backgroundType={backgroundType}>
              {mediaType === "video" ? (
                <Youtube video={video} />
              ) : (
                <Image
                  image={image.image}
                  imageText={image.imageText}
                  textBackground={image.textBackground}
                  height="100%"
                  width="100%"
                  objectFit="contain"
                />
              )}
            </S.ImageContainer>
            <Flex $column $gap={24}>
              <H3
                color={
                  backgroundColor === "yellow"
                    ? "brown"
                    : isLight(backgroundColor)
                      ? "black"
                      : "creme"
                }
              >
                {title}
              </H3>
              <QuoteTypography>
                <Wysiwyg
                  html={quote}
                  color={
                    backgroundColor === "yellow"
                      ? "brown"
                      : isLight(backgroundColor)
                        ? "black"
                        : "creme"
                  }
                />
              </QuoteTypography>
              <Body
                size={16}
                color={
                  backgroundColor === "yellow"
                    ? "brown"
                    : isLight(backgroundColor)
                      ? "black"
                      : "creme"
                }
              >
                {text}
              </Body>
            </Flex>
          </S.CardInner>
        </S.Inner>
      </S.Card>
    )
  );

  return (
    <S.Section id={blockId} as={motion.section} {...motionAnimation}>
      <S.Wrapper
        $backgroundColor={backgroundColor}
        $backgroundType={backgroundType}
      >
        {slides && (
          <S.IconContainer $backgroundType={backgroundType}>
            <MainInner>
              <Flex $justify="between">
                <div
                  onClick={() => {
                    if (currentSlide === 0) {
                      scrollToSlide(quotes.length - 1);
                      return;
                    }
                    scrollToSlide(currentSlide - 1);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <Icon src="ArrowLeft" color={textColor} width={24} />
                </div>
                <div
                  onClick={() => {
                    if (currentSlide === quotes.length - 1) {
                      scrollToSlide(0);
                      return;
                    }
                    scrollToSlide(currentSlide + 1);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <Icon src="ArrowRight" color={textColor} width={24} />
                </div>
              </Flex>
            </MainInner>
          </S.IconContainer>
        )}

        <S.ScrollContainer
          onScroll={handleScroll}
          ref={scrollContainerRef}
          data-scroll-container
        >
          {renderQuotes}
        </S.ScrollContainer>

        {slides && (
          <S.Pagination>
            {quotes.map((_, i) => (
              <S.Dot
                key={i}
                $active={currentSlide === i}
                onClick={() => scrollToSlide(i)}
                $backgroundColor={backgroundColor}
              />
            ))}
          </S.Pagination>
        )}
      </S.Wrapper>
    </S.Section>
  );
};

export default Quote;
